import React, { useRef, useLayoutEffect } from 'react';
import { animate, scroll } from 'motion';
import { theme } from '@fiverr-private/theme';
import { Container } from '@fiverr-private/layout_components';
import DragScrollCarousel, { ExtendedResponsiveSpacing, ExtendedResponsiveWidth } from '../../DragScrollCarousel';

export interface CategoryPreviewsLayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  carouselClassName?: string;
}

const HEADER_HEIGHT = 80;

interface GalleryGapType {
  default: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: string;
  xxxl: string;
}

const galleryGap: GalleryGapType = {
  default: 3,
  sm: 6,
  md: 5,
  lg: 8,
  xl: 10,
  xxl: '52px',
  xxxl: '72px',
};

const paddingSpacer = {
  default: '27px',
  sm: '20px',
  md: '22px',
  lg: '33px',
  xl: '52px',
  xxl: '70px',
  xxxl: '93px',
};

const visibleCards = {
  default: 1.5,
  md: 2.5,
};

interface ContainerWithCleanup extends HTMLDivElement {
  __cleanup?: () => void;
}

const CategoryPreviewsLayout: React.FC<CategoryPreviewsLayoutProps> = ({ children, header, carouselClassName }) => {
  const containerRef = useRef<ContainerWithCleanup>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (window.innerWidth < theme.numericBreakpoints.lg) {
      return;
    }

    const container = containerRef.current;
    const carousel = carouselRef.current;

    if (!container || !carousel) {
      return;
    }

    setTimeout(() => {
      const getScrollableElement = () => {
        const allElements = carousel.querySelectorAll('*');

        for (const element of allElements) {
          const el = element as HTMLElement;
          const styles = window.getComputedStyle(el);

          if (styles.overflowX === 'auto' || styles.overflowX === 'scroll') {
            return el;
          }
        }

        return null;
      };

      const scrollableEl = getScrollableElement();

      if (!scrollableEl) {
        return;
      }

      const carouselItems = carousel.querySelectorAll('[data-carousel-item]');
      const itemsCount = carouselItems.length;

      if (itemsCount === 0) {
        return;
      }

      const totalScrollDistance = scrollableEl.scrollWidth - scrollableEl.clientWidth;

      if (totalScrollDistance <= 0) {
        return;
      }

      const viewportHeight = window.innerHeight - HEADER_HEIGHT;
      const scrollSpeed = 0.5;
      const requiredScrollHeight = totalScrollDistance / scrollSpeed;

      const minHeight = viewportHeight * 2;
      const containerHeight = Math.max(minHeight, requiredScrollHeight);

      container.style.height = `${containerHeight}px`;
      scrollableEl.style.overflow = 'visible';

      const carouselWrapper = scrollableEl;

      const horizontalAnimation = animate(
        carouselWrapper,
        {
          transform: [`translateX(0px)`, `translateX(-${totalScrollDistance}px)`],
        },
        {
          ease: 'linear',
        }
      );

      scroll(horizontalAnimation, {
        target: container,
        offset: [`start ${HEADER_HEIGHT}px`, `${containerHeight - viewportHeight}px ${HEADER_HEIGHT}px`],
      });

      container.__cleanup = () => {
        horizontalAnimation.stop();
        horizontalAnimation.cancel();
        container.style.height = 'auto';
        scrollableEl.style.overflow = 'auto';
        carouselWrapper.style.transform = '';
      };
    }, 100);

    return () => {
      if (container?.__cleanup) {
        container.__cleanup();
      }
    };
  }, []);

  const getItemWidth = (gap: string, padding: string, visibleCards: number) =>
    `calc((100vw - ${padding} * 2 - ${gap} * 2) / ${visibleCards})`;

  return (
    <Container ref={containerRef} position="relative" width="100%" height={{ default: 'auto', lg: 'auto' }}>
      <Container position={{ default: 'static', lg: 'sticky' }} top="80px" width="100%" zIndex="2">
        {header && header}
        <Container ref={carouselRef} width="100%">
          <DragScrollCarousel
            gap={galleryGap as unknown as ExtendedResponsiveSpacing}
            itemWidth={
              {
                default: getItemWidth(theme.spacing[galleryGap.default], paddingSpacer.default, visibleCards.default),
                sm: getItemWidth(theme.spacing[galleryGap.sm], paddingSpacer.sm, visibleCards.default),
                md: getItemWidth(theme.spacing[galleryGap.md], paddingSpacer.md, visibleCards.md),
                lg: getItemWidth(theme.spacing[galleryGap.lg], paddingSpacer.lg, visibleCards.md),
                xl: getItemWidth(theme.spacing[galleryGap.xl], paddingSpacer.xl, visibleCards.md),
                xxl: getItemWidth(galleryGap.xxl, paddingSpacer.xxl, visibleCards.md),
                xxxl: getItemWidth(galleryGap.xxxl, paddingSpacer.xxxl, visibleCards.md),
              } as unknown as ExtendedResponsiveWidth
            }
            className={carouselClassName}
            showFadeOverlay={false}
            paddingSpacers={false}
            disableWheel={true}
            disableScroll={true}
          >
            {React.Children.toArray(children)}
          </DragScrollCarousel>
        </Container>
      </Container>
    </Container>
  );
};

export default CategoryPreviewsLayout;
